import { useEffect, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { useNotifications } from '../../../store/useStore';
import { EmailPreview } from '../EmailPreview';
import {
  templateService,
  type AmbitoModello,
  type EmailTemplate,
} from '../../../lib/email/templateService';

interface SceltaModelloDialogProps {
  aperto: boolean;
  onChiudi: (aperto: boolean) => void;
  /** Quali modelli proporre. */
  ambito: AmbitoModello;
  /** Il modello scelto. L'uso viene gia' conteggiato qui. */
  onScelto: (modello: EmailTemplate) => void;
  /** Avvisa che il contenuto attuale verra' sostituito. */
  sovrascrive?: boolean;
}

function quandoUsato(modello: EmailTemplate): string {
  if (modello.usi_count === 0) return 'mai usato';
  const volte = modello.usi_count === 1 ? 'usato una volta' : `usato ${modello.usi_count} volte`;
  if (!modello.ultimo_uso_at) return volte;
  return `${volte}, l'ultima il ${new Date(modello.ultimo_uso_at).toLocaleDateString('it-IT')}`;
}

/**
 * La scelta di un modello da cui ripartire.
 *
 * A sinistra l'elenco, a destra come viene: un nome da solo non dice mai
 * abbastanza per scegliere, e scoprire di aver preso quello sbagliato dopo
 * l'invio e' tardi.
 */
export function SceltaModelloDialog({
  aperto,
  onChiudi,
  ambito,
  onScelto,
  sovrascrive,
}: SceltaModelloDialogProps) {
  const { addNotification } = useNotifications();

  const [modelli, setModelli] = useState<EmailTemplate[]>([]);
  const [sceltoId, setSceltoId] = useState<string | null>(null);
  const [caricando, setCaricando] = useState(true);

  useEffect(() => {
    if (!aperto) return;
    setCaricando(true);
    templateService
      .elenco(ambito)
      .then((elenco) => {
        setModelli(elenco);
        setSceltoId(elenco[0]?.id ?? null);
      })
      .catch((errore) =>
        addNotification({
          type: 'error',
          title: 'Non riesco a leggere i modelli',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        })
      )
      .finally(() => setCaricando(false));
  }, [aperto, ambito, addNotification]);

  const scelto = modelli.find((m) => m.id === sceltoId) ?? null;

  const usa = () => {
    if (!scelto) return;
    templateService.segnaUso(scelto.id);
    onScelto(scelto);
    onChiudi(false);
  };

  return (
    <Dialog open={aperto} onOpenChange={onChiudi}>
      <DialogContent className="max-w-5xl sm:h-[82vh] flex flex-col overflow-y-hidden gap-0">
        <DialogHeader className="flex-shrink-0 pb-3 border-b border-gray-100">
          <DialogTitle>Parti da un modello</DialogTitle>
          <p className="text-xs text-neutral-500">
            {sovrascrive
              ? 'Quello che hai scritto finora viene sostituito.'
              : 'Il contenuto viene copiato: modificarlo qui non tocca il modello.'}
          </p>
        </DialogHeader>

        <DialogBody className="pt-3">
          {caricando ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : modelli.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
              <p className="text-sm text-neutral-600 font-medium">Nessun modello</p>
              <p className="text-xs text-neutral-400 mt-1 max-w-sm mx-auto leading-relaxed">
                Si creano dalle campagne o dalle sequenze: componi una email e premi
                "Salva come modello".
              </p>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)] h-full">
              <div className="space-y-1.5 lg:overflow-y-auto lg:pr-1">
                {modelli.map((modello) => (
                  <button
                    key={modello.id}
                    type="button"
                    onClick={() => setSceltoId(modello.id)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-lg border transition-colors',
                      sceltoId === modello.id
                        ? 'border-primary-400 bg-primary-50/50'
                        : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <span className="block text-xs font-semibold text-gray-900 truncate">
                      {modello.name}
                    </span>
                    <span className="block text-[11px] text-neutral-500 truncate mt-0.5">
                      {modello.subject || 'senza oggetto'}
                    </span>
                    <span className="block text-[11px] text-neutral-400 mt-1">
                      {quandoUsato(modello)}
                    </span>
                  </button>
                ))}
              </div>

              <div className="min-w-0 min-h-0">
                {scelto && (
                  <>
                    {scelto.description && (
                      <p className="text-xs text-neutral-500 mb-2 leading-relaxed">
                        {scelto.description}
                      </p>
                    )}
                    <EmailPreview
                      documento={scelto.document}
                      oggetto={scelto.subject}
                      preheader={scelto.preheader ?? ''}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter className="flex-shrink-0 border-t border-gray-100 pt-3">
          <Button variant="outline" onClick={() => onChiudi(false)}>
            Annulla
          </Button>
          <Button
            onClick={usa}
            disabled={!scelto}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            Usa questo modello
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
